/**
 * @author Radek Liška <ing.radek.liska@gmail.com>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

// @ts-check

import CommunicatorNumber from '../communicator/number/communicator-number';
import CommunicatorUtility from '../communicator/utility/communicator-utility';

/**
 * @import { CommunicatorSettingsGetter } from '../communicator/settings/communicator-settings'
 * @import { ContentInfo } from '../content/info/content-info'
 * @import AutorebootManager from './autoreboot-manager'
 */

/**
 * @typedef {Pick<ContentInfo, 'communicatorSettings'>} IContentInfo
 */

export const settingsKeyAutorebootDisabled = 'autoreboot.disabled';
export const settingsKeyAutorebootInterval = 'autoreboot.interval';

/**
 * Nahrazovač pro autoreboot, který provádí synchronizaci časovače automatického
 * restartu zařízení, dle nastavení v obsahu zařízení.
 */
export default class AutorebootReplacer {
  /**
   * @param {boolean} defaultDisabled Výchozí hodnota deaktivace
   * preventivní restartu.
   * @param {number} defaultInterval Výchozí interval, ve kterém se zařízení
   * preventivně restartuje. [ms]
   * @param {AutorebootManager} manager
   */
  constructor(defaultDisabled, defaultInterval, manager) {
    this.defaultDisabled = defaultDisabled;
    this.defaultInterval = defaultInterval;
    this.manager = manager;
    /** @type {number | undefined} */
    this.interval = undefined;
  }

  /**
   * @private
   * @param {CommunicatorSettingsGetter} settings
   * @returns {number} Interval preventivního restartu v ms ze settings nebo this.defaultInterval.
   */
  getInterval(settings) {
    const value = settings.get(settingsKeyAutorebootInterval);
    const hours = CommunicatorNumber.fromStringOrNull(value);
    if (hours === null) {
      return this.defaultInterval;
    }
    return hours * 60 * 60 * 1000;
  }

  /**
   * @private
   * @param {CommunicatorSettingsGetter} settings
   * @returns {boolean} Nastavení deaktivace preventivní restartu ze settings nebo this.defaultDisabled.
   */
  isDisabled(settings) {
    const value = settings.get(settingsKeyAutorebootDisabled);
    return CommunicatorUtility.parseBoolean(value) ?? this.defaultDisabled;
  }

  /**
   * Nastaví časovače dle obsahu zařízení.
   * @param {IContentInfo} contentInfo
   * @returns {Promise<void>}
   */
  async replace(contentInfo) {
    const { manager } = this;

    const { communicatorSettings } = contentInfo;
    if (this.isDisabled(communicatorSettings)) {
      this.interval = undefined;
      // Pokud je autoreboot zakázaný, pokuze se zastaví časovač.
      return manager.stop();
    }
    const interval = this.getInterval(communicatorSettings);
    if (this.interval !== interval) {
      this.interval = interval;
      manager.stop();
      return await manager.start(interval);
    }
  }
}
